import {
  addAtom,
  calcDepictedGeometry,
  clearChirality,
  clearGeometry,
  editAtom,
  editBond,
  flipAlongAxis,
  flipBondDirection,
  flipGeometry,
  getSubset,
  mergeMol,
  relax,
  removeSubset,
  rotateAroundBond,
  setPositions,
  setUnchangedPositions,
} from '@moleculeone/draw';
import _ from 'lodash';

import { calculateSeededPositions } from '@f/utils/mol';

import {
  CLEAR_CHARGE,
  CLEAR_CHIRAL_BOND,
  CLEAR_GEOMETRY_BOND,
  COPY,
  CUT,
  ESCAPE,
  FLIP_BOND_DIRECTION,
  FLIP_BOND_GEOMETRY,
  FLIP_MOL,
  MODIFY_CHARGE,
  MOL_MOUSE_EVENT,
  MOUSE_DOWN,
  MOUSE_MOVE,
  MOUSE_UP,
  PICK_ELEMENT,
  REDO,
  REDRAW,
  RELAX,
  REMOVE,
  ROTATE_AROUND_BOND,
  SELECT_ALL,
  SET_CHIRAL_BOND,
  SET_ELEMENT,
  SET_FRAGMENT_ATOM_MODE,
  SET_FRAGMENT_BOND_MODE,
  SET_GEOMETRY_BOND,
  SET_MODE,
  SET_MOL,
  SET_MOLS,
  UNDO,
} from '../../etc/constants';
import { updateMols, updateSelected } from '../state';

import { CLIPBOARD_PASTE_FULFILL } from './molecule-object.actions';
import { removeAtomOrBond } from './molecule-object.utils';
import { defaultMode, fragmentAtomMode, fragmentBondMode, mappedTraits } from './reducer/modes';
import { mouseOverAtom, mouseOverBond, mouseOverEmptySpace } from './reducer/selectors';

export function mouseEffect(state, effect) {
  if (effect.type !== MOL_MOUSE_EVENT) {
    return {};
  }

  const mouse = state.mouse;
  const { event, molIndex } = effect;

  switch (event.type) {
    case MOUSE_DOWN:
      return { mouse: { lastDown: event, lastMove: event, molIndex } };
    case MOUSE_MOVE:
      return {
        mouse: {
          lastDown: mouse.lastDown,
          lastUp: mouse.lastUp,
          lastMove: event,
          molIndex,
        },
      };
    case MOUSE_UP:
      if (mouse.lastDown != null) {
        return {
          mouse: {
            lastDown: mouse.lastDown,
            lastUp: event,
            lastMove: event,
            molIndex,
          },
        };
      }
      return { mouse: { lastMove: event, molIndex } };

    default:
      throw new Error(`Illegal mouse event type: ${event.type}.`);
  }
}

export function modifyEffect(state, effect) {
  const { mols, mouse, selected, history } = state;
  const { molIndex } = mouse;
  const mol = mols[molIndex];

  const atom = mouseOverAtom(state);
  const empty = mouseOverEmptySpace(state);

  let newMol = mol;

  switch (effect.type) {
    case MODIFY_CHARGE:
    case CLEAR_CHARGE:
      if (atom != null) {
        const newCharge = effect.type === CLEAR_CHARGE ? 0 : atom.charge + effect.change;
        newMol = editAtom(mol, atom.idx, null, newCharge);
        break;
      }
      return {};
    case SET_ELEMENT:
      if (atom != null) {
        newMol = editAtom(mol, atom.idx, effect.element, null);
        break;
      }
      if (empty && effect.mouse != null) {
        newMol = addAtom(mol, effect.mouse.svgPos, effect.element)[0];
        break;
      }
      return {};
    case SET_MOL:
      if (effect.data.molIndex !== undefined) {
        return {
          mols: updateMols(mols, effect.data.mol, effect.data.molIndex),
        };
      }
      if (effect.data.useActiveMol) {
        newMol = effect.data.mol;
        break;
      }
      return { mols: mols.map(() => _.cloneDeep(effect.data.mol)) };
    case SET_MOLS: {
      const newHistory = history;
      const newMols = _.cloneDeep(effect.data.mols);
      if (effect.data.initial) {
        newHistory.current = 0;
        newHistory.list = [
          {
            mols: newMols,
            selected: [[], [], []],
          },
        ];
      }
      return { mols: newMols, history: newHistory };
    }
    case PICK_ELEMENT:
      if (effect.event.atom && mol.atoms[effect.event.idx] != null) {
        newMol = editAtom(mol, effect.event.idx, effect.element);
        break;
      }
      if (effect.event.empty) {
        newMol = addAtom(mol, effect.event.svgPos, effect.element)[0];
        break;
      }
      return {};
    case SET_CHIRAL_BOND:
      newMol = editBond(mol, effect.idx, 1, effect.chiralType);
      break;
    case SET_GEOMETRY_BOND: {
      const geometry = calcDepictedGeometry(mol, effect.idx);
      newMol = editBond(mol, effect.idx, 2, null, geometry);
      break;
    }
    case FLIP_BOND_GEOMETRY: {
      const geometry = flipGeometry(mol.bonds[effect.idx].geometry);
      newMol = editBond(mol, effect.idx, 2, null, geometry);
      break;
    }
    case ROTATE_AROUND_BOND: {
      const start = mol.bonds[effect.idx].start;
      const end = mol.bonds[effect.idx].end;
      const positions = rotateAroundBond(mol, start, end);
      newMol = setPositions(mol, positions, false);
      break;
    }
    case CLEAR_GEOMETRY_BOND:
      newMol = clearGeometry(mol, effect.idx);
      break;
    case FLIP_BOND_DIRECTION:
      newMol = flipBondDirection(mol, effect.idx);
      break;
    case CLEAR_CHIRAL_BOND:
      newMol = clearChirality(mol, effect.idx);
      break;
    case RELAX: {
      const weights = {};

      if (selected.length > 0) {
        for (const key of Object.keys(mol.atoms)) {
          weights[key] = 10000;
        }

        for (const key of selected) {
          weights[key] = 1;
        }
      }

      newMol = relax(mol, weights, { iters: 15 });
      break;
    }
    case REDRAW:
      if (effect.molIndex !== undefined) {
        newMol = setPositions(
          mols[effect.molIndex],
          calculateSeededPositions({
            mol: mols[effect.molIndex],
            seedId: effect.smiles,
            incrementSeed: true,
          })
        );
        return { mols: updateMols(mols, newMol, effect.molIndex) };
      }
      return {
        mols: mols.map(mol =>
          setPositions(
            mol,
            calculateSeededPositions({
              mol,
              seedId: effect.smiles,
              incrementSeed: true,
            })
          )
        ),
      };
    case FLIP_MOL:
      newMol = setUnchangedPositions(mol, flipAlongAxis(mol.positions, effect.axis));
      break;
    default:
      return {};
  }

  return { mols: updateMols(mols, newMol, molIndex) };
}

export function removeEffect(state, effect) {
  if (effect.type !== REMOVE) {
    return {};
  }

  const { mols, selected, mouse } = state;
  const { molIndex } = mouse;
  const mol = mols[molIndex];
  const selection = selected[molIndex];

  const atom = mouseOverAtom(state);
  const bond = mouseOverBond(state);

  const newMol = removeAtomOrBond(mol, atom, bond, [...selection]);

  return {
    mols: updateMols(mols, newMol, molIndex),
    selected: updateSelected(selected, selection.length === 0 ? selection : [], molIndex),
  };
}

export function clipboardEffect(state, effect, action) {
  const { mols, selected, mouse } = state;
  const { molIndex } = mouse;
  const mol = mols[molIndex];
  const selection = selected[molIndex];

  switch (effect.type) {
    case COPY:
      return { clipboard: getSubset(mol, selection) };
    case CUT:
      return {
        clipboard: getSubset(mol, selection),
        mols: updateMols(mols, removeSubset(mol, selection), molIndex),
        selected: updateSelected(selected, [], molIndex),
      };
    case CLIPBOARD_PASTE_FULFILL:
      return {
        mols: updateMols(mols, mergeMol(mol, action.payload.result).newMol, molIndex),
      };
    case SELECT_ALL:
      return {
        selected: updateSelected(selected, Object.keys(mol.atoms), molIndex),
      };
    default:
      return {};
  }
}

export function modeEffect(state, effect) {
  const { type, fragment, idx, mode } = effect;

  switch (type) {
    case SET_FRAGMENT_ATOM_MODE:
      return { mode: fragmentAtomMode.atomFragmentData(fragment, idx) };
    case SET_FRAGMENT_BOND_MODE:
      return { mode: fragmentBondMode.bondFragmentData(fragment, idx) };
    case SET_MODE:
      return { mode: mappedTraits[mode].initial };
    default:
      return {};
  }
}

export function escapeEffect(state, effect) {
  if (effect.type !== ESCAPE) {
    return {};
  }

  const { selected, mouse } = state;
  const { molIndex } = mouse;
  const selection = selected[molIndex];

  return {
    mode: defaultMode.trait.initial,
    selected: updateSelected(selected, selection.length === 0 ? selection : [], molIndex),
  };
}

export function historyEffect(state, effect) {
  const { mouse } = state;
  const { list, current } = state.history;
  const { type } = effect;

  if (type === UNDO && current > 0) {
    const oldState = list[current - 1];

    return {
      mouse: {
        molIndex: mouse.molIndex,
      },
      selected: oldState.selected,
      mols: oldState.mols,
      history: {
        current: current - 1,
        list,
      },
    };
  }

  if (type === REDO && current < list.length - 1) {
    const nextState = list[current + 1];

    return {
      mouse: {
        molIndex: mouse.molIndex,
      },
      selected: nextState.selected,
      mols: nextState.mols,
      history: {
        current: current + 1,
        list,
      },
    };
  }

  return {};
}

export function updateHistory(state) {
  const { mols, selected, history } = state;
  const { list, current } = history;

  const potentialNewHistoryItem = {
    mols,
    selected,
  };
  if (!_.isEqual(list[current], potentialNewHistoryItem)) {
    return {
      history: {
        current: current + 1,
        list: [...list.slice(0, current + 1), potentialNewHistoryItem],
      },
    };
  }

  return {};
}
